import * as angular from 'angular';
import * as moment from 'moment';

import { IDateAlignmentHelper } from '../../common/helpers/dateAlignment/dateAlignmentHelper';
import { IApplicationService } from '../data/application.service';
import { ICurrentApplicationService } from '../data/currentApplication.service';
import { IWorkspaceService } from '../general/workspace.service';
import { IFrequency, IApplicationConfiguration, DayOfWeek, TimeUnit } from '../../models';

export interface IDateAlignmentHelperService {
    getNext(input: moment.Moment): angular.IPromise<moment.Moment>;
    getNextSync(input: moment.Moment, config: IApplicationConfiguration): moment.Moment;
    next(input: moment.Moment, freq: IFrequency, dayBeginTime: moment.Duration, dayOfWeek: DayOfWeek, localTimeZone: string): moment.Moment;
    getNextForAggregation(input: moment.Moment, interval: number, unit: TimeUnit): angular.IPromise<moment.Moment>;
    getStartOfWeek(input: moment.Moment): angular.IPromise<moment.Moment>;
    getPrevious(input: moment.Moment): angular.IPromise<moment.Moment>;
    getPreviousSync(input: moment.Moment, config: IApplicationConfiguration): moment.Moment;
    previous(input: moment.Moment, freq: IFrequency, dayBeginTime: moment.Duration, dayOfWeek: DayOfWeek, localTimeZone: string, isEvenIfAligned?: boolean): moment.Moment;
    getPreviousForAggregation(input: moment.Moment, interval: number, unit: TimeUnit): angular.IPromise<moment.Moment>;
    getRange(beginDate: Date, endDate: Date, frequency: IFrequency, excludeEndDate: boolean): angular.IPromise<Date[]>;
    getRangeSync(beginDate: Date, endDate: Date, frequency: IFrequency, appConfiguration: IApplicationConfiguration, excludeEndDate?: boolean): Date[];
}

export class DateAlignmentHelperService implements IDateAlignmentHelperService {
    static serviceId = 'dateAlignmentHelperService';

    static $inject = [
        '$q',
        'workspaceService',
        'applicationService',
        'dateAlignmentHelper',
        'currentApplicationService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _workspaceService: IWorkspaceService,
        private _applicationService: IApplicationService,
        private _dateAlignmentHelper: IDateAlignmentHelper,
        private _currentApplicationService: ICurrentApplicationService
    ) {
    }

    getNext(input: moment.Moment): angular.IPromise<moment.Moment> {
        return this.getApplicationConfiguration()
            .then(config => this.getNextSync(input, config));
    }

    getNextSync(input: moment.Moment, config: IApplicationConfiguration): moment.Moment {
        return this.next(
            input,
            { interval: config.interval, intervalTimeUnit: config.intervalTimeUnit },
            config.beginDayTime,
            config.weekBeginDay,
            config.timeZone
        );
    }

    next(input: moment.Moment, freq: IFrequency, dayBeginTime: moment.Duration, dayOfWeek: DayOfWeek, localTimeZone?: string): moment.Moment {
        return this._dateAlignmentHelper
            .computeNext(input, dayBeginTime, freq.interval, freq.intervalTimeUnit, dayOfWeek, localTimeZone);
    }

    previous(input: moment.Moment, freq: IFrequency, dayBeginTime: moment.Duration, dayOfWeek: DayOfWeek, localTimeZone?: string, doPreviousEvenIfAligned?: boolean): moment.Moment {
        return this._dateAlignmentHelper
            .computePrevious(input, dayBeginTime, freq.interval, freq.intervalTimeUnit, dayOfWeek, localTimeZone, doPreviousEvenIfAligned);
    }

    getPreviousForAggregation(input: moment.Moment, interval: number, unit: TimeUnit): angular.IPromise<moment.Moment> {
        return this.getApplicationConfiguration()
            .then(conf =>
                this._dateAlignmentHelper.computePrevious(input, conf.beginDayTime, interval, unit, conf.weekBeginDay, conf.timeZone)
            );
    }

    getPrevious(input: moment.Moment): angular.IPromise<moment.Moment> {
        return this.getApplicationConfiguration()
            .then(config => this.getPreviousSync(input, config));
    }

    getPreviousSync(input: moment.Moment, config: IApplicationConfiguration): moment.Moment {
        return this._dateAlignmentHelper
            .computePrevious(input, config.beginDayTime, config.interval, config.intervalTimeUnit, config.weekBeginDay, config.timeZone);
    }

    getNextForAggregation(input: moment.Moment, interval: number, intervalTimeUnit: TimeUnit): angular.IPromise<moment.Moment> {
        return this.getApplicationConfiguration()
            .then(config =>
                this.next(input, { interval, intervalTimeUnit }, config.beginDayTime, config.weekBeginDay, config.timeZone)
            );
    }

    // TODO why using a promise at all ?
    private getApplicationConfiguration(): angular.IPromise<IApplicationConfiguration> {
        const defered = this._$q.defer<IApplicationConfiguration>();
        let result = this._currentApplicationService.getApplicationConfiguration();
        if (result) {
            defered.resolve(result);
        } else {
            const workspace = this._workspaceService.getWorkspace();
            if (workspace) {
                this._applicationService.getByName(workspace.application)
                    .then(data => this._currentApplicationService.setApplication(data))
                    .then(() => {
                        result = this._currentApplicationService.getApplicationConfiguration();
                        defered.resolve(result);
                    });
            } else {
                defered.reject('not connected');
            }
        }
        return defered.promise;
    }

    getStartOfWeek(input: moment.Moment): angular.IPromise<moment.Moment> {
        return this.getApplicationConfiguration()
            .then(config => {
                let previous = input.clone().day(config.weekBeginDay);
                if (previous > input) {
                    const duration = moment.duration(1, 'w');
                    previous = previous.subtract(duration);
                }
                const next = this.next(
                    previous,
                    { interval: config.interval, intervalTimeUnit: config.intervalTimeUnit },
                    config.beginDayTime,
                    config.weekBeginDay,
                    config.timeZone
                );
                return moment(next.toISOString());
            });
    }

    getRange(beginDate: Date, endDate: Date, frequency: IFrequency, excludeEndDate: boolean = false): angular.IPromise<Date[]> {
        return this.getApplicationConfiguration()
            .then(data => this.getRangeSync(beginDate, endDate, frequency, data, excludeEndDate));
    }

    getRangeSync(beginDate: Date, endDate: Date, frequency: IFrequency, config: IApplicationConfiguration, excludeEndDate?: boolean): Date[] {
        const { beginDayTime, timeZone, weekBeginDay } = config;
        const { interval, intervalTimeUnit } = frequency;

        let dates: Date[] = [];

        let currentMoment = moment(endDate);
        const beginMoment = moment(beginDate);
        while (currentMoment >= beginMoment) {
            const m = currentMoment.clone();
            const isoString = m.toISOString();
            dates.push(m.toDate());
            currentMoment = this._dateAlignmentHelper.computePrevious(m, beginDayTime, interval, intervalTimeUnit, weekBeginDay, timeZone);
            if (currentMoment.isAfter(m)) { // Avoid freezing the widget if DST change error
                throw 'Previous date is after current date';
            }
            if (currentMoment.toISOString() === isoString) {
                // Infinite loop...
                throw 'Infinite loop';
            }
        }
        dates = dates.reverse();
        if (excludeEndDate) {
            dates.pop();
        }
        return dates;
    }
}